import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Theme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DropdownInput from "common/components/DropdownInput";
import { useTranslation } from "common/locales";
const PREFIX = "GenericDropdown";

const classes = {
  formControl: `${PREFIX}-formControl`,
};

const StyledFormControl = styled(FormControl)(
  ({ theme }: { theme: Theme }) => ({
    [`&.${classes.formControl}`]: {
      maxWidth: 400,
      paddingBottom: theme.spacing(2),
    },
  })
);

interface GenericDropdownProps<T> {
  currentValue?: T;
  disabled: boolean;
  onChange: (e: SelectChangeEvent<T>) => void;
  inputError: boolean;
  label: string;
  name: string;
  possibleValues: { [key: string]: string };
  tPrefix: string;
  required?: boolean;
  shouldTranslate?: boolean;
  flipKeyValue?: boolean;
  itemDisabled?: { [key: string]: string };
}

export {};

export default function GenericDropdown<T>({
  disabled,
  onChange,
  currentValue,
  inputError,
  label,
  name,
  possibleValues,
  tPrefix,
  required,
  shouldTranslate = true,
  flipKeyValue = false,
  itemDisabled,
}: GenericDropdownProps<T>) {
  const { t } = useTranslation();

  const possibleValuesAsKVArray = Object.entries(possibleValues).map(
    ([key, value]) => [key, value]
  );

  possibleValuesAsKVArray.sort((a, b) => a[1].localeCompare(b[1]));

  return (
    <StyledFormControl
      disabled={disabled}
      error={inputError}
      fullWidth
      className={classes.formControl}
    >
      <InputLabel
        id="yield-unit-type-select-label"
        variant="standard"
        shrink={true}
        error={inputError}
        required={required ?? true}
      >
        {t(label)}
      </InputLabel>
      <Select
        labelId="yield-unit-type-select-label"
        id="yield-unit-type-select"
        value={currentValue ?? ("" as unknown as T)}
        onChange={onChange}
        input={<DropdownInput />}
        error={inputError}
        name={name}
      >
        {possibleValuesAsKVArray.map(([key, value]) => {
          let itemDisabledReason = "";
          if (itemDisabled) {
            itemDisabledReason = itemDisabled[key] ?? "";
          }
          const itemIsDisabled = itemDisabledReason !== "";
          let label = "";
          if (shouldTranslate) {
            label = t(`${tPrefix}.${value}`);
          } else {
            label = value;
          }
          if (itemIsDisabled && itemDisabledReason) {
            label += ` (${itemDisabledReason})`;
          }
          return (
            <MenuItem
              value={flipKeyValue ? key : value}
              key={flipKeyValue ? value : key}
              disabled={itemIsDisabled}
            >
              {label}
            </MenuItem>
          );
        })}
      </Select>
    </StyledFormControl>
  );
}
